
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResourceGrid from "./ResourceGrid";
import { ResourceData } from "../data/resources";
import AlgorithmPlayground from "./AlgorithmPlayground";
import MLTheoryCheatsheet from "./MLTheoryCheatsheet";
import { Code, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTimedAccess } from "@/context/TimedAccessContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { BugReportDialog } from "@/components/BugReportDialog";

interface CategoryTabsProps {
  categories: string[];
  filteredResources: ResourceData[];
}

const CategoryTabs = ({ categories, filteredResources }: CategoryTabsProps) => {
  const { isSubscribed } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  const navigate = useNavigate();
  const hasPremiumAccess = isSubscribed || hasFreeAccess;

  return (
    <Tabs defaultValue="All" className="mb-8">
      <TabsList className="mb-4 flex flex-wrap h-auto">
        {categories.map(category => (
          <TabsTrigger
            key={category}
            value={category}
            className={
              category === "Interactive Playground"
                ? "relative text-purple-600 hover:text-purple-700 hover:scale-105 transition-all duration-500 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-2 border-purple-400/30 shadow-lg shadow-purple-500/20 animate-[pulse_5s_ease-in-out_infinite] hover:animate-none"
                : ""
            }
          >
            {category === "Interactive Playground" && (
              <Code className="h-3 w-3 mr-1 animate-[bounce_4s_ease-in-out_infinite]" />
            )}
            {category}
            {category === "Interactive Playground" && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-[ping_4s_cubic-bezier(0,0,0.2,1)_infinite] absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      
        {categories.map(category => (
          <TabsContent key={category} value={category}>
            {category === "Interactive Playground" ? (
              hasPremiumAccess ? (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <BugReportDialog />
                  </div>
                  <AlgorithmPlayground />
                </div>
              ) : (
                <div className="text-center py-12 px-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Lock className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Premium Feature</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    The Interactive ML Coding Playground is available exclusively for premium subscribers.
                    Upgrade now to practice coding ML algorithms interactively.
                  </p>
                  <Button onClick={() => navigate('/get-premium')} size="lg">
                    Upgrade to Premium
                  </Button>
                </div>
              )
            ) : category === "ML Theory Cheatsheet" ? (
              <MLTheoryCheatsheet />
            ) : category === "Interviews (Coming Soon)" ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-medium mb-2">Content coming soon!</h3>
                <p className="text-muted-foreground">
                  We're preparing comprehensive interview resources. Subscribe to get notified when they're available.
                </p>
            </div>
          ) : (
            <ResourceGrid 
              resources={filteredResources.filter(resource => 
                category === "All" || resource.category === category
              )} 
            />
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default CategoryTabs;
